import React, { useEffect } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import { getWeeksArray } from '@/store/slices/utilsSlice';
import { OFFSET_MINUS, OFFSET_PLUS, SET_NEXT_WEEK, SET_PREVIOUS_WEEK } from '@/config/variables';
import { ArrowDropUp } from '@/assets/icons';
import { useAppDispatch, useAppSelector, usePrevious, useWeek } from '@/hooks';
import { selectWeeks } from '@/store/selectors/utilsSelectors';

const complianceTableWeekSelector = createSelector(selectWeeks, weeks => ({ weeks }));

const ComplianceTableWeekSelector = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { weeks } = useAppSelector(complianceTableWeekSelector);
  const { selectedWeekId, selectWeek } = useWeek();

  const prevWeekId = usePrevious(selectedWeekId);

  useEffect(() => {
    if (!selectedWeekId) {
      return;
    }
    if (!Object.keys(weeks).length) {
      dispatch(
        getWeeksArray({
          weekId: selectedWeekId,
          offsetMinus: OFFSET_MINUS,
          offsetPlus: OFFSET_PLUS,
        }),
      );
      return;
    }

    // load missing weeks if needed
    const isPrevButtonClicked = prevWeekId !== undefined && prevWeekId > selectedWeekId;
    const hasOffsetFromPast = weeks[selectedWeekId - 1];
    const hasOffsetFromFuture = weeks[selectedWeekId + 1];

    const loadMissingOffsets =
      (isPrevButtonClicked && !hasOffsetFromPast) || (!isPrevButtonClicked && !hasOffsetFromFuture);

    if (!selectedWeekId || !loadMissingOffsets) {
      return;
    }

    const promise = dispatch(
      getWeeksArray({
        weekId: selectedWeekId,
        offsetMinus: OFFSET_MINUS,
        offsetPlus: OFFSET_PLUS,
      }),
    );

    return () => {
      promise.abort();
    };
  }, [selectedWeekId]);

  const handleChangeWeekId = (weekOffset: number) => {
    const newWeekId = selectedWeekId + weekOffset;

    if (weeks[newWeekId]) {
      selectWeek(newWeekId);
    }
  };

  return (
    <>
      <PreviousWeek onClick={() => handleChangeWeekId(SET_PREVIOUS_WEEK)} />
      <NextWeek onClick={() => handleChangeWeekId(SET_NEXT_WEEK)} />
    </>
  );
};

const PreviousWeek = styled.div`
  width: 10px;
  height: 7px;
  background-color: #0087dd;
  mask-image: url(${ArrowDropUp});
  mask-repeat: no-repeat;
  mask-position: left;
  mask-size: contain;
  cursor: pointer;
`;

const NextWeek = styled(PreviousWeek)`
  position: absolute;
  bottom: -20px;
  transform: rotate3d(0, 0, 1, 180deg);
`;

export { ComplianceTableWeekSelector };
